import { WitboostVersionedEntity } from '@agilelab/plugin-wb-builder-common';
import {
  CollapseButton,
  customAlertApiRef,
  useEnvironmentsContext,
  WbCard,
  WbButton,
  WbCardContent,
  WbEnvironmentSelector,
  WbCardActions,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { builderDpPoliciesTest } from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import PlayIcon from '@material-ui/icons/PlayCircleFilled';
import { format, parseISO } from 'date-fns';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { panelCatalogApiRef } from '../../../api';
import { generateTestTooltip } from '../../common/utils';
import { TestTable } from '../TestTable/TestTable';
import { useControlPanel } from '../useControlPanel';
import { TestCardProvider, useTestCard } from './useTestCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    select: {
      minWidth: 140,
      marginLeft: theme.spacing(1),
    },
    cardContent: {
      display: 'flex',
      height: '500px',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    buttonsWrapper: {
      display: 'flex',
      alignItems: 'baseline',
    },
  }),
);

export function LaunchTestButton(props: {
  previewDescriptor: string | null | undefined;
  isLoadingDescriptor: boolean;
}) {
  const { previewDescriptor, isLoadingDescriptor } = props;
  const { entity } = useControlPanel();
  const { fetchTests } = useTestCard();
  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const configApi = useApi(configApiRef);
  const { setDisabledStatus } = useEnvironmentsContext();

  const [loading, setLoading] = useState(false);

  const launchPoliciesTests = useCallback(async () => {
    if (!previewDescriptor || isLoadingDescriptor) {
      alertApi.post({
        message: 'Cannot launch policies tests without a descriptor.',
        severity: 'error',
      });
      return;
    }
    setLoading(true);
    setDisabledStatus(true);
    if (entity) {
      try {
        const useAsyncValidation = configApi.getOptionalBoolean(
          'catalog.enableAsyncValidation',
        );
        if (useAsyncValidation) {
          await panelCatalog.validateDescriptorAsync(
            previewDescriptor,
            entity?.metadata.name,
            entity?.kind,
          );
        } else {
          await panelCatalog.validateDescriptor(
            previewDescriptor,
            entity?.metadata.name,
            entity.kind,
          );
        }
        fetchTests();
      } catch (error) {
        alertApi.post({
          error,
          severity: 'error',
        });
      } finally {
        setLoading(false);
        setDisabledStatus(false);
      }
    }
  }, [
    previewDescriptor,
    isLoadingDescriptor,
    setDisabledStatus,
    entity,
    alertApi,
    configApi,
    fetchTests,
    panelCatalog,
  ]);

  const { allowed: canLaunchTest } = usePermission({
    permission: builderDpPoliciesTest,
    resourceRef: stringifyEntityRef({
      kind: entity?.kind ?? 'system',
      namespace: 'default',
      name: entity?.metadata.name ?? '',
    }),
  });

  return (
    <WbTooltip
      title={generateTestTooltip(canLaunchTest, undefined, isLoadingDescriptor)}
    >
      <span>
        <WbButton
          icon={<PlayIcon />}
          label="Test"
          onClick={launchPoliciesTests}
          loading={loading}
          disabled={loading}
        />
      </span>
    </WbTooltip>
  );
}

export function ReleaseSelect() {
  const theme = useTheme();
  const classes = useStyles();
  const { releases, selectedRelease, setSelectedRelease } = useControlPanel();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== 'HEAD') {
      setSelectedRelease(
        releases?.find(r => r.metadata.name === event.target.value) || null,
      );
    } else {
      setSelectedRelease(null);
    }
  };

  return (
    <Box className={classes.header}>
      <Box className={classes.buttonsWrapper}>
        <WbTooltip title="Select the release that will be used for tests and to display the descriptor. HEAD refers to the code currently found on the main repository branch.">
          <Typography component="span" style={{ fontWeight: 500 }}>
            Release:
          </Typography>
        </WbTooltip>

        <TextField
          id="select-release"
          inputProps={{
            'aria-label': 'select release',
          }}
          select
          size="small"
          value={selectedRelease?.metadata.name || 'HEAD'}
          onChange={handleChange}
          className={classes.select}
        >
          {releases &&
            releases.map(r => (
              <MenuItem key={r.metadata.name} value={r.metadata.name}>
                <strong
                  style={{
                    fontSize: 14,
                    color: r.metadata.isSnapshot
                      ? theme.palette.accent.main
                      : theme.palette.primary.main,
                  }}
                >
                  {r.metadata.version}
                </strong>
                <Typography
                  variant="caption"
                  component="span"
                  style={{ marginLeft: theme.spacing(1) }}
                >
                  (
                  {format(
                    parseISO(r.metadata.createdAt),
                    'yyyy/MM/dd HH:mm:ss',
                  )}
                  )
                </Typography>
              </MenuItem>
            ))}
          <MenuItem key="HEAD" value="HEAD">
            <Typography component="span" color="primary">
              HEAD
            </Typography>
            <Typography
              variant="caption"
              component="span"
              style={{ marginLeft: theme.spacing(1) }}
            >
              (actual)
            </Typography>
          </MenuItem>
        </TextField>
      </Box>
    </Box>
  );
}

export function TestCard(props: { onCollapse: () => void }) {
  const classes = useStyles();
  const {
    environment,
    entity,
    releases,
    fetchReleaseState,
    selectedRelease,
    fetchPreviewDescriptor,
    fetchPreviewDescriptorState,
  } = useControlPanel();

  useEffect(() => {
    fetchPreviewDescriptor();
  }, [environment, fetchPreviewDescriptor, selectedRelease]);

  if (!releases || !environment) return <></>;

  if (fetchReleaseState.loading) {
    return (
      <Card>
        <CardContent className={classes.cardContent}>
          <CircularProgress size={60} />
        </CardContent>
      </Card>
    );
  }

  return (
    <TestCardProvider
      entity={entity as WitboostVersionedEntity}
      environment={environment}
      release={selectedRelease}
    >
      <WbCard
        title="Test"
        cardStyle={{ height: '100%' }}
        leading={<CollapseButton onClick={props.onCollapse} />}
        actions={
          <WbCardActions breakpoint="w_xs">
            <WbEnvironmentSelector />
            <LaunchTestButton
              previewDescriptor={fetchPreviewDescriptorState.value}
              isLoadingDescriptor={fetchPreviewDescriptorState.loading}
            />
          </WbCardActions>
        }
      >
        <WbCardContent style={{ height: '100%' }}>
          <Box display="flex" flexDirection="column" height="100%">
            <TestTable />
          </Box>
        </WbCardContent>
      </WbCard>
    </TestCardProvider>
  );
}
